import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  ArrowRight, 
  Settings, 
  Clock, 
  Calendar, 
  Repeat, 
  Play, 
  PauseCircle,
  Database,
  FileText,
  BarChart2,
  Filter as FilterIcon,
  Trash2,
  RefreshCw,
  Layers,
  GitMerge,
  Bot,
  Percent,
  Calculator,
  Scissors,
  TrendingUp,
  Users,
  Target,
  Search,
  Briefcase,
  X,
  MoveVertical,
  Network,
  SplitSquareHorizontal,
  LayoutGrid,
  SlidersHorizontal,
  MessageSquare,
  Share2,
  ChevronRight,
  Star,
  ListChecks,
  LineChart,
  PieChart,
  BarChart,
  Zap,
  AlertTriangle,
  HelpCircle,
  MessageCircle,
  GitCompare,
  UserCheck,
  TrendingDown,
  BarChart3,
  Mail,
  CalendarClock
} from 'lucide-react';
import MobileCarousel from './ui/MobileCarousel';

interface WorkflowStep {
  name: string;
  description: string;
  icon: React.ReactNode;
}

interface WorkflowTemplate {
  id: number;
  name: string;
  description: string;
  lastRun: string;
  status: string;
  steps: number;
  icon: React.ReactNode;
  workflow: WorkflowStep[];
}

interface Job {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  longDescription: string;
  useCases: string[];
  configuration: Record<string, any>;
}

interface TemplateCardProps {
  template: WorkflowTemplate;
  onClick: (id: number) => void;
}

interface JobCardProps {
  job: Job;
  onClick: (id: string) => void;
}

const TemplateCard: React.FC<TemplateCardProps> = React.memo(({ template, onClick }) => (
  <div 
    className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden hover:shadow-xl transition-shadow duration-300 p-6 cursor-pointer flex flex-col h-[280px]"
    onClick={() => onClick(template.id)}
    role="button"
    aria-label={`View details for ${template.name} template`}
    tabIndex={0}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick(template.id);
      }
    }}
  >
    <div className="flex items-start mb-4">
      <div className="flex-shrink-0 mr-4 p-3 bg-gray-50 rounded-lg" aria-hidden="true">
        {template.icon}
      </div>
      <div>
        <h2 className="text-lg font-bold text-gray-900">{template.name}</h2>
      </div>
    </div>
    
    <p className="text-sm text-gray-600 flex-grow line-clamp-5">{template.description}</p>
    
    <div className="mt-4 pt-4 border-t border-gray-100">
      <div className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center ml-auto">
        Learn more
        <ArrowRight className="w-4 h-4 ml-1" aria-hidden="true" />
      </div>
    </div>
  </div>
));

const JobCard: React.FC<JobCardProps> = React.memo(({ job, onClick }) => (
  <div 
    className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden hover:shadow-lg transition-shadow duration-300 p-6 cursor-pointer flex flex-col h-[280px]"
    onClick={() => onClick(job.id)}
    role="button"
    aria-label={`View details for ${job.name} job`}
    tabIndex={0}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick(job.id);
      }
    }}
  >
    <div className="flex items-start mb-4">
      <div className="flex-shrink-0 mr-4 p-3 bg-gray-50 rounded-lg" aria-hidden="true">
        {job.icon}
      </div>
      <div>
        <h2 className="text-lg font-bold text-gray-900 mb-1">{job.name}</h2>
      </div>
    </div>
    
    <p className="text-sm text-gray-600 flex-grow line-clamp-5">{job.description}</p>
    
    <div className="mt-4 pt-4 border-t border-gray-100">
      <div className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center">
        Learn more
        <ArrowRight className="w-4 h-4 ml-1" aria-hidden="true" />
      </div>
    </div>
  </div>
));

// Add display names for debugging
TemplateCard.displayName = 'TemplateCard';
JobCard.displayName = 'JobCard';

// Optimize the grid view with virtualization
const VirtualizedGrid: React.FC<{
  items: (WorkflowTemplate | Job)[];
  renderItem: (item: WorkflowTemplate | Job) => React.ReactNode;
  columns: number;
}> = React.memo(({ items, renderItem, columns }) => {
  const [visibleRange, setVisibleRange] = useState({ start: 0, end: 12 }); // Show 12 items initially
  const gridRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleRange(prev => ({
              start: Math.max(0, prev.start - 6),
              end: Math.min(items.length, prev.end + 6)
            }));
          }
        });
      },
      { threshold: 0.1 }
    );

    if (gridRef.current) {
      observer.observe(gridRef.current);
    }

    return () => observer.disconnect();
  }, [items.length]);

  const visibleItems = items.slice(visibleRange.start, visibleRange.end);

  return (
    <div 
      ref={gridRef}
      className="grid gap-4 grid-cols-2 lg:grid-cols-3"
      style={{
        minHeight: `${Math.ceil(items.length / columns) * 300}px`, // Approximate height
        position: 'relative'
      }}
    >
      {visibleItems.map((item, index) => (
        <div
          key={item.id || index}
          style={{
            position: 'absolute',
            top: `${Math.floor((index + visibleRange.start) / columns) * 300}px`,
            left: `${((index + visibleRange.start) % columns) * (100 / columns)}%`,
            width: `calc(${100 / columns}% - 8px)`, // Account for gap
            height: '280px'
          }}
        >
          {renderItem(item)}
        </div>
      ))}
    </div>
  );
});

VirtualizedGrid.displayName = 'VirtualizedGrid';

// Optimize the carousel view
const OptimizedCarousel: React.FC<{
  items: (WorkflowTemplate | Job)[];
  renderItem: (item: WorkflowTemplate | Job) => React.ReactNode;
}> = React.memo(({ items, renderItem }) => {
  const [visibleRange, setVisibleRange] = useState({ start: 0, end: 5 }); // Show 5 items initially
  const carouselRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleRange(prev => ({
              start: Math.max(0, prev.start - 2),
              end: Math.min(items.length, prev.end + 2)
            }));
          }
        });
      },
      { threshold: 0.1 }
    );

    if (carouselRef.current) {
      observer.observe(carouselRef.current);
    }

    return () => observer.disconnect();
  }, [items.length]);

  const visibleItems = items.slice(visibleRange.start, visibleRange.end);

  return (
    <div 
      ref={carouselRef}
      className="overflow-x-auto scrollbar-hide scroll-smooth pb-8 -mx-4 snap-x snap-mandatory"
    >
      <div className="flex px-4" style={{ gap: '16px' }}>
        {visibleItems.map((item, index) => (
          <div 
            key={item.id || index}
            className="flex-shrink-0 snap-center"
            style={{ width: '85vw' }}
          >
            {renderItem(item)}
          </div>
        ))}
      </div>
    </div>
  );
});

OptimizedCarousel.displayName = 'OptimizedCarousel';

const WorkflowJobs: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'templates' | 'custom'>('templates');
  const [selectedJob, setSelectedJob] = useState<string | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<number | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [isAutoScrolling, setIsAutoScrolling] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);
  const autoScrollInterval = useRef<NodeJS.Timeout | null>(null);

  const workflowTemplates = [
    {
      id: 1,
      name: "Automatic Feature Prioritization",
      description: "Automatically prioritize features based on customer feedback and business impact.",
      lastRun: "2 hours ago",
      status: "Active",
      steps: 5,
      icon: <BarChart2 className="w-8 h-8 text-blue-500" />,
      workflow: [
        {
          name: "Extract Features",
          description: "Identify feature requests and product enhancements from customer feedback using AI.",
          icon: <Bot className="w-8 h-8 text-blue-500" />
        },
        {
          name: "Calculate Impact",
          description: "Analyze customer sentiment and frequency of mentions to determine potential impact.",
          icon: <Star className="w-8 h-8 text-amber-500" />
        },
        {
          name: "Assess Feasibility",
          description: "Evaluate implementation complexity and resource requirements.",
          icon: <ListChecks className="w-8 h-8 text-green-500" />
        },
        {
          name: "Compute Priority Score",
          description: "Apply weighted scoring algorithm to balance impact and feasibility.",
          icon: <Calculator className="w-8 h-8 text-purple-500" />
        },
        {
          name: "Generate Prioritized List",
          description: "Create ranked feature list with supporting data for decision-making.",
          icon: <BarChart className="w-8 h-8 text-indigo-500" />
        }
      ]
    },
    {
      id: 2,
      name: "Extract Customer Needs",
      description: "Identify and categorize customer needs from support tickets, surveys, and feedback to inform product development.",
      lastRun: "45 minutes ago",
      status: "Active",
      steps: 6,
      icon: <Users className="w-10 h-10 text-green-500" />,
      workflow: [
        {
          name: "Collect Feedback",
          description: "Gather customer feedback from multiple channels including support tickets and surveys.",
          icon: <Database className="w-8 h-8 text-blue-500" />
        },
        {
          name: "Clean & Prepare Data",
          description: "Remove duplicates and standardize format for consistent analysis.",
          icon: <Trash2 className="w-8 h-8 text-red-500" />
        },
        {
          name: "Extract Key Themes",
          description: "Use semantic clustering to identify common needs and pain points.",
          icon: <Network className="w-8 h-8 text-violet-500" />
        },
        {
          name: "Categorize Needs",
          description: "Organize identified needs into product areas and feature categories.",
          icon: <Layers className="w-8 h-8 text-amber-500" />
        },
        {
          name: "Quantify Importance",
          description: "Analyze frequency and sentiment to determine priority of each need.",
          icon: <PieChart className="w-8 h-8 text-green-500" />
        }
      ]
    },
    {
      id: 3,
      name: "Process Requests by Company Goal",
      description: "Align customer requests with company objectives to ensure development efforts support strategic initiatives.",
      lastRun: "1 hour ago",
      status: "Active",
      steps: 7,
      icon: <Target className="w-10 h-10 text-purple-500" />,
      workflow: [
        {
          name: "Import Company Goals",
          description: "Load strategic objectives and KPIs from company documentation.",
          icon: <Target className="w-8 h-8 text-purple-500" />
        },
        {
          name: "Process Customer Requests",
          description: "Extract and normalize customer feature requests and feedback.",
          icon: <Users className="w-8 h-8 text-blue-500" />
        },
        {
          name: "Map Requests to Goals",
          description: "Use AI to connect customer requests with relevant company objectives.",
          icon: <GitMerge className="w-8 h-8 text-teal-500" />
        },
        {
          name: "Score Strategic Alignment",
          description: "Calculate how well each request supports company goals.",
          icon: <Calculator className="w-8 h-8 text-orange-500" />
        },
        {
          name: "Generate Alignment Report",
          description: "Create visualization showing how requests support strategic initiatives.",
          icon: <LineChart className="w-8 h-8 text-indigo-500" />
        }
      ]
    },
    {
      id: 4,
      name: "Competitive Research Processor",
      description: "Analyze competitor products, features, and market positioning to identify opportunities and threats.",
      lastRun: "3 days ago",
      status: "Active",
      steps: 8,
      icon: <Search className="w-10 h-10 text-orange-500" />,
      workflow: [
        {
          name: "Gather Competitor Data",
          description: "Collect information from competitor websites, reviews, and industry reports.",
          icon: <Search className="w-8 h-8 text-orange-500" />
        },
        {
          name: "Extract Feature Sets",
          description: "Identify and categorize features offered by competitors.",
          icon: <ListChecks className="w-8 h-8 text-blue-500" />
        },
        {
          name: "Compare Pricing Models",
          description: "Analyze pricing strategies and value propositions across competitors.",
          icon: <Briefcase className="w-8 h-8 text-green-500" />
        },
        {
          name: "Identify Gaps & Opportunities",
          description: "Discover unmet needs and potential competitive advantages.",
          icon: <Zap className="w-8 h-8 text-amber-500" />
        },
        {
          name: "Generate Competitive Matrix",
          description: "Create comprehensive comparison of your product vs. competitors.",
          icon: <BarChart2 className="w-8 h-8 text-indigo-500" />
        }
      ]
    },
    {
      id: 5,
      name: "Risk Assessment",
      description: "Identify and evaluate potential risks in product development, customer feedback, and market trends to inform strategic decisions.",
      lastRun: "1 day ago",
      status: "Active",
      steps: 5,
      icon: <AlertTriangle className="w-10 h-10 text-red-500" />,
      workflow: [
        {
          name: "Collect Risk Indicators",
          description: "Gather data from customer complaints, support tickets, and market reports.",
          icon: <Database className="w-8 h-8 text-blue-500" />
        },
        {
          name: "Identify Risk Patterns",
          description: "Use AI to detect recurring issues and potential problem areas.",
          icon: <Network className="w-8 h-8 text-purple-500" />
        },
        {
          name: "Calculate Risk Scores",
          description: "Assign severity and probability scores to each identified risk.",
          icon: <Calculator className="w-8 h-8 text-red-500" />
        },
        {
          name: "Prioritize Risk Factors",
          description: "Rank risks based on potential impact and likelihood of occurrence.",
          icon: <BarChart className="w-8 h-8 text-orange-500" />
        },
        {
          name: "Generate Risk Report",
          description: "Create comprehensive risk assessment with recommended mitigation strategies.",
          icon: <FileText className="w-8 h-8 text-indigo-500" />
        }
      ]
    },
    {
      id: 6,
      name: "Most Asked Questions",
      description: "Automatically identify and analyze frequently asked questions from customer interactions to improve documentation and support resources.",
      lastRun: "5 hours ago",
      status: "Active",
      steps: 5,
      icon: <HelpCircle className="w-10 h-10 text-amber-500" />,
      workflow: [
        {
          name: "Extract Questions",
          description: "Identify questions from support tickets, chat logs, and community forums.",
          icon: <MessageCircle className="w-8 h-8 text-blue-500" />
        },
        {
          name: "Group Similar Questions",
          description: "Use semantic clustering to identify common question themes and variations.",
          icon: <Network className="w-8 h-8 text-violet-500" />
        },
        {
          name: "Analyze Frequency",
          description: "Calculate how often each question type appears across all channels.",
          icon: <BarChart2 className="w-8 h-8 text-green-500" />
        },
        {
          name: "Extract Best Answers",
          description: "Identify the most helpful and accurate responses to each question.",
          icon: <Star className="w-8 h-8 text-amber-500" />
        },
        {
          name: "Generate FAQ Content",
          description: "Create structured FAQ content ready for documentation and knowledge base.",
          icon: <FileText className="w-8 h-8 text-indigo-500" />
        }
      ]
    },
    {
      id: 7,
      name: "Compare Competitive Release Notes",
      description: "Automatically analyze and compare competitor release notes to identify new features, improvements, and strategic direction changes.",
      lastRun: "2 days ago",
      status: "Active",
      steps: 5,
      icon: <GitCompare className="w-10 h-10 text-indigo-500" />,
      workflow: [
        {
          name: "Collect Release Notes",
          description: "Gather release notes from competitor websites, blogs, and app stores.",
          icon: <Database className="w-8 h-8 text-blue-500" />
        },
        {
          name: "Extract Key Updates",
          description: "Identify new features, improvements, and fixes from each competitor's releases.",
          icon: <ListChecks className="w-8 h-8 text-green-500" />
        },
        {
          name: "Categorize Changes",
          description: "Group updates by type (feature, improvement, fix) and product area.",
          icon: <Layers className="w-8 h-8 text-amber-500" />
        },
        {
          name: "Compare Across Competitors",
          description: "Analyze similarities and differences in focus areas and development pace.",
          icon: <GitCompare className="w-8 h-8 text-indigo-500" />
        },
        {
          name: "Generate Competitive Report",
          description: "Create summary of competitive landscape and strategic implications.",
          icon: <FileText className="w-8 h-8 text-purple-500" />
        }
      ]
    },
    {
      id: 8,
      name: "Weekly Stakeholder Update",
      description: "Automatically compile key metrics, achievements, and challenges into a comprehensive weekly update for stakeholders.",
      lastRun: "6 days ago",
      status: "Active",
      steps: 5,
      icon: <UserCheck className="w-10 h-10 text-green-600" />,
      workflow: [
        {
          name: "Collect Key Metrics",
          description: "Gather performance data, customer feedback, and project status updates.",
          icon: <BarChart3 className="w-8 h-8 text-blue-500" />
        },
        {
          name: "Identify Achievements",
          description: "Extract completed milestones, positive metrics, and successful initiatives.",
          icon: <Star className="w-8 h-8 text-amber-500" />
        },
        {
          name: "Analyze Challenges",
          description: "Identify issues, blockers, and areas needing attention or resources.",
          icon: <AlertTriangle className="w-8 h-8 text-red-500" />
        },
        {
          name: "Compile Action Items",
          description: "Create list of next steps, decisions needed, and upcoming deadlines.",
          icon: <CalendarClock className="w-8 h-8 text-purple-500" />
        },
        {
          name: "Schedule Stakeholder Reports",
          description: "Create custom and dynamic executive reports which are sent on a scheduled basis.",
          icon: <Mail className="w-8 h-8 text-green-600" />
        }
      ]
    },
    {
      id: 9,
      name: "Changing Trends Report",
      description: "Monitor and analyze shifting trends in customer behavior, market conditions, and product usage patterns.",
      lastRun: "1 day ago",
      status: "Active",
      steps: 5,
      icon: <TrendingDown className="w-10 h-10 text-purple-600" />,
      workflow: [
        {
          name: "Collect Time-Series Data",
          description: "Gather historical data on key metrics, customer behavior, and market indicators.",
          icon: <Database className="w-8 h-8 text-blue-500" />
        },
        {
          name: "Identify Significant Changes",
          description: "Detect statistically significant shifts and emerging patterns in the data.",
          icon: <TrendingDown className="w-8 h-8 text-red-500" />
        },
        {
          name: "Analyze Potential Causes",
          description: "Correlate changes with external events, product updates, or market shifts.",
          icon: <Search className="w-8 h-8 text-amber-500" />
        },
        {
          name: "Project Future Trends",
          description: "Forecast how identified trends might evolve based on historical patterns.",
          icon: <LineChart className="w-8 h-8 text-green-500" />
        },
        {
          name: "Generate Trends Report",
          description: "Create an executive report highlighting key changes, causes, and recommendations.",
          icon: <FileText className="w-8 h-8 text-purple-600" />
        }
      ]
    }
  ];

  const jobs = [
    {
      id: 'ai-assistant',
      name: "AI Assistant",
      description: "Guide AI to analyze or extract information from your data using custom prompts.",
      icon: <Bot className="w-8 h-8 text-emerald-500" />,
      longDescription: "The AI Assistant job allows you to leverage the power of AI to analyze your data in natural language. You can create custom prompts that guide the AI to extract specific information, identify patterns, or generate insights from your documents. This is particularly useful for unstructured data like customer feedback, support tickets, or market research reports.",
      useCases: [
        "Extract key themes from customer feedback",
        "Summarize long documents into actionable insights",
        "Identify sentiment patterns across customer interactions",
        "Generate product improvement recommendations based on user feedback"
      ],
      configuration: {
        prompt: "What insights can you provide about customer pain points?",
        model: "Advanced",
        maxTokens: 1000
      }
    },
    {
      id: 'cleanup',
      name: "Cleanup",
      description: "Clean up a table by removing specified columns, columns with a high percentage of missing values, or rows with empty values in designated columns.",
      icon: <Trash2 className="w-8 h-8 text-green-500" />,
      longDescription: "The Cleanup job helps you prepare your data for analysis by removing unnecessary or problematic data points. You can specify columns to remove, set thresholds for missing values, and define rules for handling empty values. This ensures that your downstream analysis is based on clean, reliable data.",
      useCases: [
        "General data cleaning",
        "Filter out records with incomplete information",
        "Remove columns with high missing values"
      ],
      configuration: {
        columnsToRemove: ["id", "created_at", "updated_at"],
        missingValueThreshold: 0.5,
        handleEmptyValues: "drop"
      }
    },
    {
      id: 'compute-score',
      name: "Compute Score",
      description: "Computes a score for each data record based on a user-defined mathematical expression, enabling ranking and filtering.",
      icon: <Calculator className="w-8 h-8 text-blue-500" />,
      longDescription: "The Compute Score job allows you to create custom scoring algorithms for your data. You can define mathematical expressions that combine multiple fields to generate a single score for each record. This is useful for prioritization, ranking, or filtering based on complex criteria.",
      useCases: [
        "Prioritize feature requests based on impact and effort",
        "Score leads based on engagement and conversion potential",
        "Rank support tickets by urgency and customer value",
        "Evaluate product performance across multiple metrics"
      ],
      configuration: {
        expression: "(impact * 0.7) + (feasibility * 0.3)",
        outputColumn: "priority_score",
        normalization: "0-100"
      }
    },
    {
      id: 'distribution-analysis',
      name: "Distribution Analysis",
      description: "Calculates the percentage distribution of distinct values within specified columns of a dataset.",
      icon: <Percent className="w-8 h-8 text-purple-500" />,
      longDescription: "The Distribution Analysis job helps you understand the composition of your data by calculating the frequency and percentage of different values in specified columns. This provides insights into the distribution of categories, segments, or other discrete values in your dataset.",
      useCases: [
        "Analyze customer segment distribution",
        "Understand feature request categories by frequency",
        "Identify most common customer issues by product area"
      ],
      configuration: {
        columns: ["category", "segment", "region"],
        groupBy: "category",
        sortBy: "frequency",
        visualization: "pie chart"
      }
    },
    {
      id: 'drop-duplicates',
      name: "Drop Duplicates",
      description: "Eliminates duplicate rows from the dataset to ensure data integrity and avoid redundant information.",
      icon: <Scissors className="w-8 h-8 text-red-500" />,
      longDescription: "The Drop Duplicates job helps maintain data integrity by identifying and removing duplicate records from your dataset. You can specify which columns to consider when identifying duplicates and choose how to handle them (keep first, keep last, etc.).",
      useCases: [
        "Remove duplicate customer records after merging databases",
        "Ensure unique feature requests in prioritization workflows",
        "Clean survey responses with multiple submissions",
        "Deduplicate product catalog entries"
      ],
      configuration: {
        columns: ["email", "customer_id"],
        keepStrategy: "first",
        ignoreCase: true
      }
    },
    {
      id: 'extract-insights',
      name: "Extract Insights",
      description: "Extracts meaningful insights, such as sentiment, topics, and features.",
      icon: <BarChart2 className="w-8 h-8 text-indigo-500" />,
      longDescription: "The Extract Insights job uses advanced natural language processing to automatically identify key information from text data. It can extract sentiment, identify topics, recognize entities, and detect features mentioned in customer feedback, support tickets, or other text sources.",
      useCases: [
        "Analyze customer sentiment across feedback channels",
        "Identify trending topics in support conversations",
        "Extract product features mentioned in reviews",
        "Detect competitor mentions in market research"
      ],
      configuration: {
        extractors: ["sentiment", "topics", "entities", "features"],
        language: "English",
        minConfidence: 0.7
      }
    },
    {
      id: 'group-by',
      name: "Group By",
      description: "Groups the dataset by a specified column and aggregates the values in the remaining columns.",
      icon: <Layers className="w-8 h-8 text-amber-500" />,
      longDescription: "The Group By job allows you to segment your data based on specified columns and perform aggregation operations on the remaining columns. This is useful for summarizing data, calculating statistics by category, or preparing data for visualization.",
      useCases: [
        "Calculate average customer satisfaction by product category",
        "Group feature requests by department and priority",
        "Analyze support ticket resolution time by issue type"
      ],
      configuration: {
        groupByColumns: ["category", "region"],
        aggregations: {
          "revenue": "sum",
          "customers": "count",
          "satisfaction": "average"
        }
      }
    },
    {
      id: 'json-transformer',
      name: "JSON Transformer",
      description: "Converts JSON data to a structured dataset.",
      icon: <FileText className="w-8 h-8 text-orange-500" />,
      longDescription: "The JSON Transformer job helps you work with JSON data by converting it into a structured format that's easier to analyze. It can flatten nested JSON structures, extract specific fields, and normalize complex data into tabular format.",
      useCases: [
        "Transform exported data from SaaS platforms",
        "Prepare JSON logs for business intelligence",
        "Integrate data from multiple JSON sources"
      ],
      configuration: {
        jsonPath: "$.data.items",
        flattenNested: true,
        includeFields: ["id", "name", "metrics.*"],
        outputFormat: "csv"
      }
    },
    {
      id: 'limit',
      name: "Limit",
      description: "Limits the number of rows to process.",
      icon: <FilterIcon className="w-8 h-8 text-gray-500" />,
      longDescription: "The Limit job allows you to restrict the number of records processed in your workflow. This is useful for testing workflows with a sample of data, focusing on the most recent or relevant records, or managing processing resources for large datasets.",
      useCases: [
        "Test workflow logic with a small sample before full processing",
        "Focus analysis on most recent customer feedback",
        "Process top N records by a specific metric",
        "Manage computational resources for large datasets"
      ],
      configuration: {
        limit: 1000,
        offset: 0,
        orderBy: "created_at",
        direction: "desc"
      }
    },
    {
      id: 'merge-datasets',
      name: "Merge Datasets",
      description: "Combines data from multiple sources into a unified dataset for analysis.",
      icon: <GitMerge className="w-8 h-8 text-teal-500" />,
      longDescription: "The Merge Datasets job allows you to combine data from multiple sources based on common fields. You can perform different types of joins (inner, left, right, full) and specify how to handle conflicts or missing data.",
      useCases: [
        "Combine customer data from CRM and support systems",
        "Merge product usage data with customer feedback",
        "Integrate sales data with marketing campaign metrics",
        "Create comprehensive tables from multiple data sources"
      ],
      configuration: {
        datasets: ["customers", "orders"],
        joinType: "left",
        joinColumns: {
          "customers": "customer_id",
          "orders": "customer_id"
        },
        conflictResolution: "latest"
      }
    },
    {
      id: 'filter',
      name: "Filter",
      description: "Filters the dataset based on specified conditions to focus on relevant data for analysis.",
      icon: <SlidersHorizontal className="w-8 h-8 text-blue-400" />,
      longDescription: "The Filter job allows you to narrow down your dataset by applying conditions to include or exclude records based on specific criteria. You can use common operators like equals, contains, greater than, less than, is null, and more to create precise filters that focus your analysis on the most relevant data.",
      useCases: [
        "Focus analysis on high-value customers or transactions",
        "Exclude test or incomplete records from reports",
        "Analyze feedback with specific sentiment or keywords",
        "Filter data by date ranges, regions, or categories"
      ],
      configuration: {
        conditions: [
          {
            column: "customer_segment",
            operator: "equals",
            value: "enterprise"
          },
          {
            column: "feedback_score",
            operator: "greaterThan",
            value: 7
          },
          {
            column: "comment",
            operator: "contains",
            value: "feature request"
          }
        ],
        matchType: "all" // Options: "all" (AND) or "any" (OR)
      }
    },
    {
      id: 'reorder-columns',
      name: "Reorder Columns",
      description: "Rearranges the columns in a specific order to facilitate data exploration and analysis.",
      icon: <MoveVertical className="w-8 h-8 text-cyan-500" />,
      longDescription: "The Reorder Columns job allows you to reorganize the structure of your dataset by changing the order of columns. This makes it easier to focus on the most important fields first and improves the readability of your data for both analysis and presentation.",
      useCases: [
        "Prioritize key metrics for better decision making",
        "Group related fields together for easier analysis",
        "Standardize column order across multiple datasets",
        "Prepare data for export to other systems"
      ],
      configuration: {
        columnOrder: ["customer_id", "name", "email", "subscription_type", "revenue"]
      }
    },
    {
      id: 'semantic-clustering',
      name: "Semantic Clustering",
      description: "Groups similar text data points together based on their semantic or statistical similarity, revealing hidden patterns and insights.",
      icon: <Network className="w-8 h-8 text-violet-500" />,
      longDescription: "The Semantic Clustering job uses advanced natural language processing to group similar text entries together based on their meaning rather than just keywords. This helps identify patterns, themes, and relationships that might not be apparent through traditional analysis methods.",
      useCases: [
        "Identify common themes in customer feedback without predefined categories",
        "Group similar feature requests to understand underlying user needs",
        "Discover emerging topics in support conversations",
        "Organize unstructured text data into meaningful segments"
      ],
      configuration: {
        textColumn: "feedback_text",
        clusteringMethod: "semantic",
        numberOfClusters: "auto",
        minClusterSize: 5
      }
    },
    {
      id: 'split-and-count',
      name: "Split and Count",
      description: "Analyzes a text column by splitting values based on a specified delimiter and then counting the occurrences of each resulting item.",
      icon: <SplitSquareHorizontal className="w-8 h-8 text-pink-500" />,
      longDescription: "The Split and Count job helps analyze text fields that contain multiple values separated by delimiters (like commas, semicolons, or spaces). It splits these values and counts how frequently each individual item appears, providing insights into the distribution and popularity of different elements.",
      useCases: [
        "Analyze tags or categories assigned to content",
        "Identify most common features mentioned in feedback",
        "Count frequency of keywords in search queries"
      ],
      configuration: {
        textColumn: "tags",
        delimiter: ",",
        trimWhitespace: true,
        caseSensitive: false
      }
    },
    {
      id: 'split-and-expand',
      name: "Split and Expand",
      description: "Splits values within a specified column based on a delimiter, creating a new row for each resulting value.",
      icon: <LayoutGrid className="w-8 h-8 text-rose-500" />,
      longDescription: "The Split and Expand job transforms your data by taking a column with multiple values (separated by a delimiter) and creating separate rows for each value. This is useful for normalizing data and preparing it for more detailed analysis where each value needs to be treated individually.",
      useCases: [
        "Convert comma-separated tags into individual records",
        "Normalize data with multiple categories per entry",
        "Prepare multi-value fields for relational database storage",
        "Transform data for more granular analysis of individual elements"
      ],
      configuration: {
        column: "categories",
        delimiter: ";",
        keepOriginalColumns: true,
        outputColumnName: "category"
      }
    }
  ];

  // Memoize handlers
  const openJobModal = useCallback((jobId: string) => {
    setSelectedJob(jobId);
    document.body.classList.add('overflow-hidden');
    // Focus the modal when it opens
    setTimeout(() => {
      const modal = document.querySelector(`[aria-labelledby="job-modal-${jobId}"]`);
      if (modal instanceof HTMLElement) {
        modal.focus();
      }
    }, 0);
  }, []);

  const closeJobModal = useCallback(() => {
    setSelectedJob(null);
    document.body.classList.remove('overflow-hidden');
  }, []);

  const openTemplateModal = useCallback((templateId: number) => {
    setSelectedTemplate(templateId);
    document.body.classList.add('overflow-hidden');
    // Focus the modal when it opens
    setTimeout(() => {
      const modal = document.querySelector(`[aria-labelledby="template-modal-${templateId}"]`);
      if (modal instanceof HTMLElement) {
        modal.focus();
      }
    }, 0);
  }, []);

  const closeTemplateModal = useCallback(() => {
    setSelectedTemplate(null);
    document.body.classList.remove('overflow-hidden');
  }, []);

  // Add keyboard event handler for modals
  const handleModalKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      if (selectedJob) {
        closeJobModal();
      } else if (selectedTemplate !== null) {
        closeTemplateModal();
      }
    }
  }, [selectedJob, selectedTemplate, closeJobModal, closeTemplateModal]);

  // Find the selected job
  const selectedJobData = jobs.find(job => job.id === selectedJob);
  
  // Find the selected template
  const selectedTemplateData = workflowTemplates.find(template => template.id === selectedTemplate);

  // Function to update scroll buttons visibility
  const updateScrollButtons = () => {
    if (!carouselRef.current) return;
    
    const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    
    // Update active slide
    const slideWidth = window.innerWidth * 0.85 + 16; // Card width + gap
    const currentSlide = Math.round(scrollLeft / slideWidth);
    setActiveSlide(currentSlide);
  };

  // Function to scroll the carousel
  const scrollCarousel = (direction: 'left' | 'right') => {
    if (!carouselRef.current) return;
    
    const scrollAmount = window.innerWidth - 32; // Full viewport width minus padding
    const { scrollLeft } = carouselRef.current;
    
    const newScroll = direction === 'left' 
      ? Math.max(0, scrollLeft - scrollAmount)
      : scrollLeft + scrollAmount;

    carouselRef.current.scrollTo({
      left: newScroll,
      behavior: 'smooth'
    });
  };

  // Function to start auto-scrolling
  const startAutoScroll = () => {
    if (isAutoScrolling) return;
    setIsAutoScrolling(true);

    autoScrollInterval.current = setInterval(() => {
      if (carouselRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
        
        // If we've reached the end, reset to the beginning
        if (scrollLeft >= scrollWidth - clientWidth - 10) {
          carouselRef.current.scrollTo({
            left: 0,
            behavior: 'smooth'
          });
        } else {
          // Scroll by one card width
          carouselRef.current.scrollTo({
            left: scrollLeft + window.innerWidth - 32,
            behavior: 'smooth'
          });
        }
      }
    }, 5000); // Scroll every 5 seconds
  };

  // Function to stop auto-scrolling
  const stopAutoScroll = () => {
    if (autoScrollInterval.current) {
      clearInterval(autoScrollInterval.current);
      autoScrollInterval.current = null;
    }
    setIsAutoScrolling(false);
  };

  // Set up carousel auto-scrolling and event listeners
  useEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel) return;

    const handleScroll = () => {
      updateScrollButtons();
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            startAutoScroll();
          } else {
            stopAutoScroll();
          }
        });
      },
      {
        threshold: 0.1
      }
    );

    observer.observe(carousel);
    carousel.addEventListener('scroll', handleScroll);

    // Initial update
    updateScrollButtons();

    return () => {
      observer.disconnect();
      carousel.removeEventListener('scroll', handleScroll);
      stopAutoScroll();
    };
  }, [activeTab]); // Re-run when tab changes

  // Handle touch events for mobile
  useEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel) return;

    let touchStartX = 0;
    
    const handleTouchStart = (e: TouchEvent) => {
      touchStartX = e.touches[0].clientX;
      stopAutoScroll();
    };
    
    const handleTouchEnd = (e: TouchEvent) => {
      const touchEndX = e.changedTouches[0].clientX;
      const diff = touchStartX - touchEndX;
      
      // If the swipe is significant enough
      if (Math.abs(diff) > 50) {
        if (diff > 0) {
          // Swipe left, scroll right
          scrollCarousel('right');
        } else {
          // Swipe right, scroll left
          scrollCarousel('left');
        }
      }
      
      // Restart auto-scrolling after a delay
      setTimeout(() => {
        startAutoScroll();
      }, 3000);
    };
    
    carousel.addEventListener('touchstart', handleTouchStart, { passive: true });
    carousel.addEventListener('touchend', handleTouchEnd, { passive: true });
    
    return () => {
      carousel.removeEventListener('touchstart', handleTouchStart);
      carousel.removeEventListener('touchend', handleTouchEnd);
    };
  }, [activeTab]); // Re-run when tab changes

  return (
    <section className="py-24 bg-white" id="workflow-jobs" aria-labelledby="workflow-title">
      <a 
        href="#workflow-jobs" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:bg-white focus:px-4 focus:py-2 focus:rounded-lg focus:shadow-lg focus:z-50"
      >
        Skip to main content
      </a>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 sm:mb-16">
          <h1 id="workflow-title" className="text-3xl sm:text-4xl font-bold mb-3 sm:mb-4">
            No-Code Data Workflows
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 max-w-4xl mx-auto px-2">
            Choose from our expert-created workflow templates or build your own with limitless possibilities. 
            Combine different jobs with AI Agents to transform your data in ways you never thought possible, 
            all without writing a single line of code.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex justify-center mb-8 sm:mb-12">
          <div 
            className="inline-flex rounded-md shadow-sm w-full sm:w-auto" 
            role="tablist"
            aria-label="Workflow types"
          >
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'templates'}
              aria-controls="templates-panel"
              id="templates-tab"
              className={`px-4 sm:px-6 py-2 sm:py-3 text-sm font-medium rounded-l-lg flex-1 sm:flex-auto ${
                activeTab === 'templates'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              } border border-gray-200`}
              onClick={() => setActiveTab('templates')}
            >
              Workflow Templates
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'custom'}
              aria-controls="custom-panel"
              id="custom-tab"
              className={`px-4 sm:px-6 py-2 sm:py-3 text-sm font-medium rounded-r-lg flex-1 sm:flex-auto ${
                activeTab === 'custom'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              } border border-gray-200`}
              onClick={() => setActiveTab('custom')}
            >
              Custom Workflows
            </button>
          </div>
        </div>

        {/* Mobile View with OptimizedCarousel */}
        <div className="md:hidden">
          {activeTab === 'templates' && (
            <div
              role="tabpanel"
              id="templates-panel"
              aria-labelledby="templates-tab"
            >
              <OptimizedCarousel
                items={workflowTemplates}
                renderItem={(template) => (
                  <TemplateCard
                    template={template as WorkflowTemplate}
                    onClick={openTemplateModal}
                  />
                )}
              />
            </div>
          )}
          
          {activeTab === 'custom' && (
            <div
              role="tabpanel"
              id="custom-panel"
              aria-labelledby="custom-tab"
            >
              <OptimizedCarousel
                items={jobs}
                renderItem={(job) => (
                  <JobCard
                    job={job as Job}
                    onClick={openJobModal}
                  />
                )}
              />
            </div>
          )}
        </div>

        {/* Desktop Grid View with VirtualizedGrid */}
        <div className="hidden md:block">
          {activeTab === 'templates' && (
            <div 
              role="tabpanel"
              id="desktop-templates-panel"
              aria-labelledby="desktop-templates-tab"
            >
              <VirtualizedGrid
                items={workflowTemplates}
                renderItem={(template) => (
                  <TemplateCard
                    template={template as WorkflowTemplate}
                    onClick={openTemplateModal}
                  />
                )}
                columns={3}
              />
            </div>
          )}

          {activeTab === 'custom' && (
            <div 
              role="tabpanel"
              id="desktop-custom-panel"
              aria-labelledby="desktop-custom-tab"
            >
              <VirtualizedGrid
                items={jobs}
                renderItem={(job) => (
                  <JobCard
                    job={job as Job}
                    onClick={openJobModal}
                  />
                )}
                columns={3}
              />
            </div>
          )}
        </div>

        {/* Job Detail Modal */}
        {selectedJob && selectedJobData && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto"
            role="dialog"
            aria-labelledby={`job-modal-${selectedJob}`}
            aria-modal="true"
            onKeyDown={handleModalKeyDown}
            tabIndex={-1}
          >
            <div 
              className="bg-white rounded-xl shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto"
              role="document"
            >
              <div className="sticky top-0 bg-white p-4 sm:p-6 border-b border-gray-100 flex justify-between items-center">
                <div className="flex items-center">
                  <div className="flex-shrink-0 mr-3 sm:mr-4" aria-hidden="true">
                    {selectedJobData.icon}
                  </div>
                  <h2 
                    id={`job-modal-${selectedJob}`}
                    className="text-xl sm:text-2xl font-bold text-gray-900"
                  >
                    {selectedJobData.name}
                  </h2>
                </div>
                <button 
                  onClick={closeJobModal}
                  className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                  aria-label="Close modal"
                >
                  <X className="w-5 h-5 sm:w-6 sm:h-6 text-gray-500" />
                </button>
              </div>
              
              <div className="p-4 sm:p-8">
                <div className="mb-8">
                  <h3 className="text-lg font-semibold mb-3">Description</h3>
                  <p className="text-gray-700">{selectedJobData.longDescription}</p>
                </div>
                
                <div className="mb-8">
                  <h3 className="text-lg font-semibold mb-3">Use Cases</h3>
                  <ul className="list-disc pl-5 space-y-2 text-gray-700">
                    {selectedJobData.useCases.map((useCase, index) => (
                      <li key={index}>{useCase}</li>
                    ))}
                  </ul>
                </div>
                
                <div className="flex justify-end">
                  <button 
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    onClick={closeJobModal}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Template Detail Modal */}
        {selectedTemplate !== null && selectedTemplateData && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto"
            role="dialog"
            aria-labelledby={`template-modal-${selectedTemplate}`}
            aria-modal="true"
            onKeyDown={handleModalKeyDown}
            tabIndex={-1}
          >
            <div 
              className="bg-white rounded-xl shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto"
              role="document"
            >
              <div className="sticky top-0 bg-white p-4 sm:p-6 border-b border-gray-100 flex justify-between items-center">
                <div className="flex items-center">
                  <div className="mr-3 sm:mr-4 p-2 sm:p-3 bg-gray-50 rounded-lg" aria-hidden="true">
                    {selectedTemplateData.icon}
                  </div>
                  <h2 
                    id={`template-modal-${selectedTemplate}`}
                    className="text-xl sm:text-2xl font-bold text-gray-900"
                  >
                    {selectedTemplateData.name}
                  </h2>
                </div>
                <button 
                  onClick={closeTemplateModal}
                  className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                  aria-label="Close modal"
                >
                  <X className="w-5 h-5 sm:w-6 sm:h-6 text-gray-500" />
                </button>
              </div>
              
              <div className="flex flex-col md:flex-row">
                {/* Left side - Description */}
                <div className="p-4 sm:p-8 md:w-1/3 border-b md:border-b-0 md:border-r border-gray-100">
                  <div className="mb-6 sm:mb-8">
                    <h2 className="text-lg font-semibold mb-3">Overview</h2>
                    <p className="text-gray-700">{selectedTemplateData.description}</p>
                  </div>
                  
                  <div className="mt-auto">
                    <button 
                      className="w-full px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      onClick={closeTemplateModal}
                    >
                      Close
                    </button>
                  </div>
                </div>
                
                {/* Right side - Workflow Steps */}
                <div className="p-4 sm:p-8 md:w-2/3 bg-gray-50">
                  <h2 className="text-lg font-semibold mb-4 sm:mb-6">Workflow Steps</h2>
                  
                  <div className="space-y-4">
                    {selectedTemplateData.workflow.map((step, index) => (
                      <div key={index} className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
                        <div className="flex items-start">
                          <div className="flex-shrink-0 mr-4">
                            {step.icon}
                          </div>
                          <div>
                            <h3 className="font-medium text-gray-900 mb-2">{step.name}</h3>
                            <p className="text-gray-600">{step.description}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

WorkflowJobs.displayName = 'WorkflowJobs';

export default WorkflowJobs; 